<template>
    <v-row justify="center">
      <v-col lg="6" md="5" class="pb-0 hidden-sm-and-down">
        <v-img class="sticky-image" height="100vh" :src="createAccountImage" />
      </v-col>
      <v-col class="register-member-aussie-times-container" lg="6" md="7" sm="12">
        <v-overlay :value="isProcessing">
          <v-progress-circular
            indeterminate
            color="primary"
            :size="70"
            :width="7"
          />
        </v-overlay>
        <v-dialog v-model="showSuccess" width="500">
          <v-card class="member-register-modal">
            <v-card-title>
              <div />
              <h2>Order Confirmed</h2>
              <div />
            </v-card-title>
            <p>Congratulations! Your order is complete. You can now access the pedigree or report online.</p>
            <v-btn
              dark
              large
              color="secondary"
              v-on:click="returnHome()"
            >RETURN HOME</v-btn>
            <v-btn
              dark
              large
              color="primary"
              v-on:click="goToReports()"
            >GO TO REPORTS LIST</v-btn>        
          </v-card>
        </v-dialog>
        <div class="top-icons">
          <a
            class="back-icon"
            v-on:click="returnHome"
          >
            <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
          </a>
        </div>
        <v-alert
          width="100%"
          v-if="!!errorMessage"
          dismissible
          type="error"
        >{{ errorMessage }}</v-alert>       
        <h1>Dog Report Order</h1>      
        <h1>for <strong>{{ dogId }}: {{ dogName }}</strong></h1>       
        <v-alert type="info">
          The online access is given immediately after the successful payment.<br/>The report can be downloaded from the dog profile or from the 'My Information' > 'Dog Reports & Pedigrees' list.
        </v-alert>         
        <v-alert type="warning">
          Note: Digital pedigrees have a lifetime access. Other dog reports can be accessed and downloaded only for a limited time, because the data gets outdated. After that period, you need to re-order them.
        </v-alert>  
        <div class="postage-container">
          <h4>DOG REPORT TYPES:</h4>
          <v-radio-group v-model="pedigreeSelected">
            <v-radio
              :label="`${pedigreeFees.large.description} - $${pedigreeFees.large.price}`"
              :value="pedigreeTypes_large"
            ></v-radio>
            <v-radio
              :label="`${pedigreeFees.small.description} - $${pedigreeFees.small.price}`"
              :value="pedigreeTypes_small"
            ></v-radio>
            <v-radio
              :label="`${pedigreeFees.digital.description} - $${pedigreeFees.digital.price}`"
              :value="pedigreeTypes_digital"
            ></v-radio>
            <v-radio
              :label="`${pedigreeFees.progenyDigital.description} - $${pedigreeFees.progenyDigital.price}`"
              :value="pedigreeTypes_progenyDigital"
            ></v-radio>
          </v-radio-group>
        </div>
        <div class="address-container" v-if="isPrintedSelection">
          <h4>MAILING ADDRESS</h4>
          <v-alert v-if="isPrintedSelection" type="info">Your order will be delivered to the address associated with your membership.<br/>Please review your mailing address for accuracy.</v-alert>
          <v-form ref="addressForm" v-model="isValidAddress" class="register-form">
            <CountryDropdown
              :selectedCountry="country"
              v-on:update="updateCountry"
            />
            <StateDropdown
              v-if="country === country_USA"
              required
              :selectedState="stateProvince"
              v-on:update="stateProvince = $event"
            />
            <v-row>
              <v-col
                :lg="country && country !== country_USA ? 8 : 12"
                :md="country && country !== country_USA ? 8 : 12"
                :sm="country && country !== country_USA ? 6 : 12"
              >
                <v-text-field
                  outlined
                  v-model="address"
                  label="Address"
                  :rules="[(v) => !!v || 'Address is required']"
                />
              </v-col>
              <v-col
                :lg="country && country !== country_USA ? 4 : 6"
                :md="country && country !== country_USA ? 4 : 6"
                sm="6">
                <v-text-field
                  outlined
                  v-model="city"
                  label="City"
                  :rules="[validateCity]"
                />
              </v-col>
              <v-col v-if="country && country !== country_USA" lg="8" md="8" sm="6" >
                <v-text-field
                  outlined
                  v-model="international"
                  label="International Area"
                  :rules="[(v) => !!v || 'International Area is required']"
                />
              </v-col>
              <v-col
                :lg="country && country !== country_USA ? 4 : 6"
                :md="country && country !== country_USA ? 4 : 6"
                sm="6">
                <v-text-field
                  outlined
                  v-model="zipPostal"
                  label="Zip/Postal"
                  :rules="[validateZip]"
                />
              </v-col>
            </v-row>
          </v-form>
        </div>
        <p><strong>All prices shown are in USD and include Postage fee for printed documents.</strong></p>
          
        <hosted-registration-payment
          class="align-self-center"
          :disabled="(isPrintedSelection && !isValidAddress)"
          buttonText="SUBMIT"
          v-on:updatePaymentInfo="pay"
        />
        <div class="footer-container">
          <v-img height="2rem" contain :src="logoImage" />
          <footer-text />
        </div>
      </v-col>
    </v-row>
  </template>
    
  <script lang="ts">
  import Vue from 'vue';

import CountryDropdown from '@/components/common/inputs/CountryDropdown.vue';
import StateDropdown from '@/components/common/inputs/StateDropdown.vue';
import FooterText from '@/components/common/FooterText.vue';
import HostedRegistrationPayment from '@/components/common/HostedRegistrationPayment.vue';

import userService from '@/services/portal/userService';
import pedigreeService from '@/services/portal/pedigreeService';
import normalizeAddressRegion from '@/helpers/normalize-address-region';
import AuthorizeNetResponseData from '@/types/AuthorizeNetResponseData';
import { Countries, PedigreeTypes } from "@/constants";
  
  import { UserInfo } from "@/types/User";
  
  const createAccountImage = require('@/assets/create-account.png');
  const logoImage = require('@/assets/logo.svg');
  
  export default Vue.extend({
    components: { FooterText, CountryDropdown, StateDropdown, HostedRegistrationPayment },
    name: 'PedigreeOrder',
    mounted: function() {
      this.initialize();
    },
    props: {
      dogId: { type: String },
      dogName: { type: String },
    },
    data: function () {
      return {
        createAccountImage,
        logoImage,
        isProcessing: false,
        errorMessage: '',
        showSuccess: false,

        country_USA: Countries.USA,    
        country_Canada: Countries.Canada,
        
        pedigreeTypes_digital: PedigreeTypes.PedigreeDigital,    
        pedigreeTypes_small: PedigreeTypes.PedigreeSmall,
        pedigreeTypes_large: PedigreeTypes.PedigreeLarge,
        pedigreeTypes_progenyDigital: PedigreeTypes.ProgenyDigital,

        country: Countries.USA,
        address: "",
        international: "",
        city: "",
        stateProvince: "",
        zipPostal: "",
        aussieTimesDisplayPrice: "",
        isValidAddress: false,
        isValidCard: false,

        pedigreeSelected: PedigreeTypes.PedigreeLarge,

        pedigreeFees: {
          digital: {description: '', price: null},
          small: {description: '', price: null},
          large: {description: '', price: null},
          progenyDigital: {description: '', price: null}
        },
      }
    },
    watch: {
      userInfo: function(newValue) {
        if (!!newValue) {
          this.initialize();
        }
      }
    },
    computed: {
      userInfo(): UserInfo {
        return this.$store.state.userInfo;
      },
      isPrintedSelection() {
        return !!this.$data.pedigreeSelected && (this.$data.pedigreeSelected == PedigreeTypes.PedigreeSmall || this.$data.pedigreeSelected == PedigreeTypes.PedigreeLarge);
      }
    },
    methods: {
      initialize: async function () {
        const userInfo = this.userInfo;
    
        if (!!userInfo) {
          this.country = userInfo.country;
          this.address = userInfo.address;
          this.international = userInfo.international;
          this.city = userInfo.city;
          this.stateProvince = userInfo.stateProvince;
          this.zipPostal = userInfo.zipPostal;
          this.normalizeAddressRegion();
          this.getFees();
        }
    },            
    getFees: async function() {
      this.isProcessing = true;

      this.pedigreeFees = await pedigreeService.getFees();

      this.isProcessing = false;
    },
    validateCity(v: string) {
      return this.country === Countries.USA
        ? !!v || 'City is required'
        : true
    },
    validateZip(v: string) {
      return this.country === Countries.USA
        ? !!v || 'Zip/Postal is required'
        : true
    },
    updateCountry: async function (v: string) {
      this.country = v;
      this.normalizeAddressRegion();

      await Vue.nextTick();

      (this.$refs.addressForm as any).validate();
    },
    returnHome() {
      this.$navigate('/');
    },
    goToReports() {
      this.$router.push('/profile/pedigrees');
    },
    pay: async function (data: AuthorizeNetResponseData) {
      this.normalizeAddressRegion();
      this.isProcessing = true;

      try {
        const fee = this.pedigreeSelected === PedigreeTypes.PedigreeDigital ? this.pedigreeFees.digital.price
                    : this.pedigreeSelected === PedigreeTypes.PedigreeSmall ? this.pedigreeFees.small.price
                    : this.pedigreeSelected === PedigreeTypes.PedigreeLarge ? this.pedigreeFees.large.price
                    : this.pedigreeSelected === PedigreeTypes.ProgenyDigital ? this.pedigreeFees.progenyDigital.price
                    : 0;
                    
        const response = await pedigreeService.orderPedigree({
          country: this.country,
          address: this.address,
          international: this.international,
          city: this.city,
          stateProvince: this.stateProvince,
          zipPostal: this.zipPostal,
          fee: fee,
          paymentInfo: data, 
          dogId: this.$props.dogId,
          pedigreePrintType: this.pedigreeSelected
        });

        if (response.success == true) {
          const newUserInfo = await userService.getProfile();

          this.$store.commit('loadUserInfo', newUserInfo);

          this.isProcessing = false;
          this.showSuccess = true;
        } else {
          this.isProcessing = false;
          this.errorMessage = response.errorMessage ? response.errorMessage : 'An Unknown Error Occured';
        }
      } catch (ex) {
        this.isProcessing = false;
        this.errorMessage = 'An Unknown Error Occured';
      }

      this.isProcessing = false;
    },
    normalizeAddressRegion: function () {
      const region = normalizeAddressRegion(
        this.country,
        this.stateProvince,
        this.international
      );
      this.stateProvince = region.state;
      this.international = region.international;
    },
  },
  });
  </script>
